import { VerificationCodeInput } from '../components/VerificationCodeInput';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Check, Eye, EyeOff } from 'lucide-react';
import { siDiscord } from 'simple-icons';
import { ApiClientError, api } from '../api';
import { useAuth } from '../auth';
import { Turnstile } from '../components/Turnstile';

interface CodeState {
  email: string;
  sent_at: string | null;
  expires_at: string | null;
  retry_after_ms: number;
  mailed?: boolean;
}

function formatRemaining(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

const discordErrors: Record<string, string> = {
  EMAIL_REGISTERED_NO_DISCORD:
    'This email is already registered. Sign in with your password. A Discord account cannot be linked to it.',
  EMAIL_PENDING_VERIFICATION:
    'An account for this email is waiting for verification. Sign in with your password and enter the code we already sent.',
  EMAIL_LINKED_TO_DISCORD:
    'This email is already registered and linked to a Discord account. Sign in with Discord.',
  NETWORK_BLOCKED: 'This network cannot create a new account.',
  ANONYMOUS_NETWORK: 'This network cannot create or use a free workspace.',
  DISPOSABLE_EMAIL: 'Use a permanent email address.',
  LOCKED_NETWORK_LIMIT: 'This network already has unpaid workspaces waiting for a plan.',
  ACCOUNT_DISABLED: 'This account is not active.',
};

export function AuthPage({ mode }: { mode: 'login' | 'signup' | 'forgot' | 'invite' }) {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [verification, setVerification] = useState<CodeState | null>(null);
  const [reset, setReset] = useState<CodeState | null>(null);
  const [resetAck, setResetAck] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);
  const [referral, setReferral] = useState('');
  const [invite, setInvite] = useState<{
    email: string;
    role: string;
    workspace_name: string;
    owner_display_name: string;
    expires_at: string;
  } | null>(null);
  const [now, setNow] = useState(Date.now());
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { refresh, user } = useAuth();
  const signup = mode === 'signup';
  const forgot = mode === 'forgot';
  const inviting = mode === 'invite';

  useEffect(() => {
    void api<{ turnstile_site_key: string }>('/auth/security-config')
      .then((result) => setTurnstileSiteKey(result.turnstile_site_key))
      .catch(() => undefined)
      .finally(() => setTurnstileReady(true));
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const discordError = query.get('error');
    if (discordError && discordErrors[discordError]) setError(discordErrors[discordError]);
    if (query.get('challenge') === '2fa') setTwoFactor(true);
    const referralCode = query.get('ref');
    if (referralCode) {
      setReferral(referralCode);
      void api(`/affiliates/claim/${encodeURIComponent(referralCode)}`).catch(() => undefined);
    }
  }, [location.search]);

  useEffect(() => {
    if (forgot) {
      void api<CodeState>('/auth/password-reset')
        .then((result) => {
          if (result.email) setReset(result);
        })
        .catch(() => undefined);
      return;
    }
    void api<CodeState>('/auth/verification')
      .then((result) => {
        if (result.email) setVerification(result);
      })
      .catch(() => undefined);
    void api<{ two_factor_required?: boolean }>('/auth/two-factor')
      .then((result) => {
        if (result.two_factor_required) setTwoFactor(true);
      })
      .catch(() => undefined);
  }, [forgot]);

  useEffect(() => {
    if (!inviting || !params.token) return;
    void api<{
      invitation: {
        email: string;
        role: string;
        workspace_name: string;
        owner_display_name: string;
        expires_at: string;
      };
    }>(`/workspace/invitations/${params.token}`)
      .then((result) => setInvite(result.invitation))
      .catch((exception: unknown) => {
        setError(exception instanceof Error ? exception.message : 'This invitation is no longer valid.');
      });
  }, [inviting, params.token]);

  useEffect(() => {
    if (!verification && !reset) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [reset, verification]);

  const retryAfterMs = useMemo(() => {
    const state = reset ?? verification;
    if (!state?.sent_at) return state?.retry_after_ms ?? 0;
    const elapsed = now - new Date(state.sent_at).getTime();
    return Math.max(0, 10 * 60 * 1000 - elapsed);
  }, [now, reset, verification]);

  const finishAuthenticated = async (next = '/dashboard') => {
    await refresh();
    const search = new URLSearchParams(location.search);
    const redirect = search.get('next');
    navigate(search.has('plan') ? '/pricing' : redirect || next);
  };

  const submitCredentials = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const form = new FormData(event.currentTarget);
    if (!turnstileToken) {
      setError('Complete the human verification challenge.');
      setLoading(false);
      return;
    }
    try {
      const result = await api<{
        verification_required?: boolean;
        two_factor_required?: boolean;
        email?: string;
        sent_at?: string | null;
        expires_at?: string | null;
        retry_after_ms?: number;
      }>(signup ? '/auth/register' : '/auth/login', {
        method: 'POST',
        body: JSON.stringify({
          email: form.get('email'),
          password: form.get('password'),
          ...(signup ? { display_name: form.get('name') } : {}),
          ...(signup && String(form.get('referral') ?? '').trim()
            ? { referral: String(form.get('referral')).trim() }
            : {}),
          turnstile_token: turnstileToken,
        }),
      });
      if (result.verification_required && result.email) {
        setVerification({
          email: result.email,
          sent_at: result.sent_at ?? null,
          expires_at: result.expires_at ?? null,
          retry_after_ms: result.retry_after_ms ?? 0,
        });
        return;
      }
      if (result.two_factor_required) {
        setTwoFactor(true);
        return;
      }
      await finishAuthenticated();
    } catch (exception: unknown) {
      if (exception instanceof ApiClientError && exception.code === 'EMAIL_PENDING_VERIFICATION') {
        navigate('/login?error=EMAIL_PENDING_VERIFICATION', { replace: true });
        return;
      } else {
        setError(exception instanceof Error ? exception.message : 'We could not complete the request.');
      }
      setTurnstileReset((value) => value + 1);
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const form = new FormData(event.currentTarget);
    if (!turnstileToken) {
      setError('Complete the human verification challenge.');
      setLoading(false);
      return;
    }
    try {
      if (twoFactor) {
        await api('/auth/two-factor/verify', {
          method: 'POST',
          body: JSON.stringify({ code: form.get('code'), turnstile_token: turnstileToken }),
        });
        await finishAuthenticated();
        return;
      }
      if (reset) {
        await api('/auth/reset-password', {
          method: 'POST',
          body: JSON.stringify({
            code: form.get('code'),
            new_password: form.get('new_password'),
            turnstile_token: turnstileToken,
          }),
        });
        setReset(null);
        navigate('/login');
        return;
      }
      await api('/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ code: form.get('code'), turnstile_token: turnstileToken }),
      });
      await finishAuthenticated();
    } catch (exception: unknown) {
      setError(exception instanceof Error ? exception.message : 'We could not verify that code.');
      setTurnstileReset((value) => value + 1);
    } finally {
      setLoading(false);
    }
  };

  const requestReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const form = new FormData(event.currentTarget);
    if (!turnstileToken) {
      setError('Complete the human verification challenge.');
      setLoading(false);
      return;
    }
    try {
      const result = await api<CodeState>('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: form.get('email'), turnstile_token: turnstileToken }),
      });
      if (result.mailed === false) {
        setReset(null);
        setResetAck(true);
      } else {
        setResetAck(false);
        setReset(result);
      }
    } catch (exception: unknown) {
      setError(exception instanceof Error ? exception.message : 'We could not start the reset.');
      setTurnstileReset((value) => value + 1);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (retryAfterMs > 0 || loading) return;
    setLoading(true);
    try {
      const path = reset ? '/auth/resend-password-reset' : '/auth/resend-verification';
      const result = await api<CodeState>(path, {
        method: 'POST',
        body: JSON.stringify({ turnstile_token: turnstileToken }),
      });
      if (reset) setReset(result);
      else setVerification(result);
    } catch (exception: unknown) {
      setError(exception instanceof Error ? exception.message : 'We could not send another code.');
      setTurnstileReset((value) => value + 1);
    } finally {
      setLoading(false);
    }
  };

  const acceptInvite = async () => {
    if (!params.token) return;
    setLoading(true);
    try {
      if (!user) {
        navigate(`/login?next=${encodeURIComponent(`/invite/${params.token}`)}`);
        return;
      }
      await api(`/workspace/invitations/${params.token}/accept`, { method: 'POST' });
      await finishAuthenticated('/dashboard/workspace');
    } catch (exception: unknown) {
      setError(exception instanceof Error ? exception.message : 'The invitation could not be accepted.');
    } finally {
      setLoading(false);
    }
  };

  const challenge = reset ?? verification;
  const asideTitle = inviting
    ? 'A workspace is waiting for you.'
    : twoFactor
      ? 'Confirm this sign-in with your authenticator.'
      : challenge
        ? reset
          ? 'Enter the reset code we sent.'
          : 'Enter the code we sent to finish opening the workspace.'
        : forgot
          ? 'Choose a new password from a code in your inbox.'
          : signup
            ? 'Put the infrastructure behind your product.'
            : 'Your files and signals are waiting.';

  return (
    <div className="auth-page">
      <div className="auth-page__aside">
        <Link className="brand brand--large" to="/">
          <span className="brand__mark">
            <i />
            <i />
            <i />
          </span>
          BCKT
        </Link>
        <div>
          <p>
            {inviting
              ? 'Workspace invitation'
              : twoFactor
                ? 'Two-factor authentication'
                : challenge
                  ? 'Check your inbox.'
                  : signup
                    ? 'One account. Three useful tools.'
                    : forgot
                      ? 'Password reset'
                      : 'Welcome back.'}
          </p>
          <h1>{asideTitle}</h1>
          {signup && !challenge && (
            <ul>
              <li>
                <Check />
                10 GB storage and 5 GB logs free
              </li>
              <li>
                <Check />
                No card required
              </li>
              <li>
                <Check />
                S3 and REST API access
              </li>
            </ul>
          )}
        </div>
        <small>Private by default · Served from the edge</small>
      </div>
      <main className="auth-card">
        {inviting ? (
          <>
            <div>
              <p>Invitation</p>
              <h2>
                {invite
                  ? `${invite.owner_display_name} invited you to ${invite.workspace_name}.`
                  : 'Open this invitation.'}
              </h2>
            </div>
            {invite && (
              <p className="auth-verify-copy">
                This invitation was sent to {invite.email} as {invite.role}. Sign in with that
                address to join. Your own workspace stays yours.
              </p>
            )}
            {location.state?.deletionRequested && <p className="auth-message" role="status">Your account is closed. We are deleting your files and logs and cancelling your subscription. We will send one final email when complete.</p>}
          {error && (
              <p className="form-error" role="alert">
                {error}
              </p>
            )}
            <button
              className="button button--accent button--full"
              onClick={() => void acceptInvite()}
              disabled={loading || !invite}
            >
              {user ? 'Accept invitation' : 'Sign in to accept'}
              <ArrowRight />
            </button>
          </>
        ) : twoFactor || challenge ? (
          <>
            <div>
              <p>{twoFactor ? 'Authenticator' : reset ? 'Reset password' : 'Verify your email'}</p>
              <h2>
                {twoFactor
                  ? 'Enter the 6-digit code from your authenticator.'
                  : `We sent a 6-digit code to ${challenge?.email}.`}
              </h2>
            </div>
            <p className="auth-verify-copy">
              {twoFactor
                ? 'A recovery code also works if you no longer have the authenticator.'
                : 'Use the same code from the original email. Closing this tab does not change it.'}
            </p>
            <form onSubmit={submitCode}>
              <label>
                <span>{twoFactor ? 'Authentication code' : 'Email code'}</span>
                <VerificationCodeInput
                  recovery={twoFactor}
                  name="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  minLength={6}
                  maxLength={20}
                  required
                  placeholder={twoFactor ? '000000' : '000 000'}
                />
              </label>
              {reset && (
                <label>
                  <span>New password</span>
                  <input
                    name="new_password" pattern=".*\S.*" title="Use 10–128 characters, not only spaces."
                    type="password"
                    autoComplete="new-password"
                    minLength={10}
                    maxLength={128}
                    required
                    placeholder="10–128 characters"
                  />
                </label>
              )}
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              {turnstileSiteKey ? (
                <Turnstile
                  siteKey={turnstileSiteKey}
                  action={twoFactor ? 'two_factor' : reset ? 'reset_password' : 'verify_email'}
                  resetSignal={turnstileReset}
                  onToken={setTurnstileToken}
                />
              ) : (
                <p className="turnstile-unavailable">
                  {turnstileReady ? 'Human verification is unavailable.' : 'Human verification is loading.'}
                </p>
              )}
              <button
                className="button button--accent button--full"
                data-state={loading ? 'loading' : undefined}
                disabled={loading || !turnstileToken}
              >
                {loading ? 'Working…' : twoFactor ? 'Confirm sign-in' : reset ? 'Set new password' : 'Confirm email'}
                <ArrowRight />
              </button>
            </form>
            {!twoFactor && (
              <p className="auth-resend">
                {retryAfterMs > 0 ? (
                  <span>Send again in {formatRemaining(retryAfterMs)}</span>
                ) : (
                  <button type="button" onClick={() => void resend()} disabled={loading || !turnstileToken}>
                    Send a new code
                  </button>
                )}
              </p>
            )}
          </>
        ) : forgot ? (
          <>
            <div>
              <p>Forgot password</p>
              <h2>
                {resetAck
                  ? 'If that account uses a password, a reset code is on its way.'
                  : 'We will email a reset code if this account uses a password.'}
              </h2>
            </div>
            {resetAck && (
              <p className="auth-verify-copy">
                Discord-only accounts do not receive a code. If you expected mail, check that inbox and the spam folder.
              </p>
            )}
            <form onSubmit={requestReset}>
              <label>
                <span>Email</span>
                <input maxLength={320} name="email" type="email" autoComplete="email" required placeholder="you@company.com" />
              </label>
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              {turnstileSiteKey ? (
                <Turnstile
                  siteKey={turnstileSiteKey}
                  action="forgot_password"
                  resetSignal={turnstileReset}
                  onToken={setTurnstileToken}
                />
              ) : (
                <p className="turnstile-unavailable">
                  {turnstileReady ? 'Human verification is unavailable.' : 'Human verification is loading.'}
                </p>
              )}
              <button className="button button--accent button--full" disabled={loading || !turnstileToken}>
                {loading ? 'Working…' : 'Send reset code'}
                <ArrowRight />
              </button>
            </form>
            <p className="auth-switch">
              Remembered it? <Link to="/login">Sign in</Link>
            </p>
          </>
        ) : (
          <>
            <div>
              <p>{signup ? 'Create your account' : 'Sign in to BCKT'}</p>
              <h2>{signup ? 'Start with the free workspace.' : 'Continue to your workspace.'}</h2>
            </div>
            <a className="button discord-button" href="/api/v1/auth/discord">
              <svg role="img" aria-label="Discord" viewBox="0 0 24 24">
                <path d={siDiscord.path} />
              </svg>
              Continue with Discord
            </a>
            <div className="auth-divider">
              <span>or use email</span>
            </div>
            <form onSubmit={submitCredentials}>
              {signup && (
                <label>
                  <span>Name</span>
                  <input name="name" pattern=".*\S.*" title="Enter a name containing visible characters." autoComplete="name" minLength={2} maxLength={80} required placeholder="Thomas" />
                </label>
              )}
              <label>
                <span>Email</span>
                <input maxLength={320} name="email" type="email" autoComplete="email" required placeholder="you@company.com" />
              </label>
              <label>
                <span>Password</span>
                <div className="password-field">
                  <input
                    name="password"
                    type={visible ? 'text' : 'password'}
                    autoComplete={signup ? 'new-password' : 'current-password'}
                    pattern={signup ? '.*\\S.*' : undefined}
                    title={signup ? 'Use 10–128 characters, not only spaces.' : undefined}
                    minLength={10}
                    maxLength={128}
                    required
                    placeholder="10–128 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setVisible((value) => !value)}
                    aria-label={visible ? 'Hide password' : 'Show password'}
                  >
                    {visible ? <EyeOff /> : <Eye />}
                  </button>
                </div>
              </label>
              {signup && <p className="auth-verify-copy">Use 10–128 characters. A long, unique passphrase is welcome. Spaces are allowed.</p>}
              {signup && (
                <label>
                  <span>Creator referral ID, optional</span>
                  <input
                    name="referral"
                    value={referral}
                    onChange={(event) => setReferral(event.target.value)}
                    maxLength={32}
                    placeholder="alberteinstein"
                    autoComplete="off"
                  />
                  <small>From bckt.io/r/alberteinstein, the ID is alberteinstein.</small>
                </label>
              )}
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}
              {turnstileSiteKey ? (
                <Turnstile
                  siteKey={turnstileSiteKey}
                  action={signup ? 'register' : 'login'}
                  resetSignal={turnstileReset}
                  onToken={setTurnstileToken}
                />
              ) : (
                <p className="turnstile-unavailable">
                  {turnstileReady ? 'Human verification is unavailable.' : 'Human verification is loading.'}
                </p>
              )}
              <button
                className="button button--accent button--full"
                data-state={loading ? 'loading' : undefined}
                disabled={loading || !turnstileToken}
              >
                {loading ? 'Working…' : signup ? 'Create account' : 'Sign in'}
                <ArrowRight />
              </button>
              <p className="auth-legal">
                By signing in or creating an account you agree to the{' '}
                <Link to="/terms">Terms</Link>, <Link to="/privacy">Privacy policy</Link>, and{' '}
                <Link to="/acceptable-use">Acceptable use policy</Link>. Essential cookies are set to
                keep the session.
              </p>
            </form>
            {!signup && (
              <p className="auth-switch">
                <Link to="/forgot-password">Forgot password</Link>
              </p>
            )}
            <p className="auth-switch">
              {signup ? 'Already have an account?' : 'New to BCKT?'}{' '}
              <Link to={signup ? '/login' : '/signup'}>{signup ? 'Sign in' : 'Create one'}</Link>
            </p>
          </>
        )}
      </main>
    </div>
  );
}
